import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { getFormattedDate } from '@lmt/unicorn-shared/app/components/patients/common';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import styles from './PatientInfo.module.scss';
import DeletedStamp from '@lmt/unicorn-shared/app/icons/archived-stamp.svg?react';
import AnonymousStamp from '@lmt/unicorn-shared/app/icons/anonymous-stamp.svg?react';
import WarningIcon from '@lmt/unicorn-shared/app/icons/exclamation_mark_red.svg?react';
import {
  localizeNumberWithSource,
  localizeSexType,
} from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { PatientDataReminder } from './PatientDataReminder';
import { QueryFactory } from '@lmt/shared-types';
import { useSubtenant } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
  useDictionaryValue,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { titleKey } from '../../../patient-form/common/common';
import { usePatientReminder } from './usePatientReminder';
import { useTranslation } from 'react-i18next';

export const PatientInfoPanel: React.FC<{ patientId: number }> = (props) => {
  const { patientId } = props;

  const i18n = useScopedTranslation('patients.overview.info');
  const i18ImportError = useScopedTranslation(
    'patients.form.edit_patient.import_errors',
  );
  const i18nDict = useTranslation(NS_DataBlockFields);
  const forms = useGlobalForms();
  const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(patientId);
  const patientInfo = patientQuery.data;
  const subtenant = useSubtenant(patientInfo?.subtenantId);

  const doctorQuery = QueryFactory.DoctorQuery.useGetDoctorQuery(
    patientQuery.data?.doctorId ?? 0,
    {
      enabled: !!patientQuery.data?.doctorId,
    },
  );
  const title = useDictionaryValue(
    CustomDictionaryName.PersonTitles,
    patientInfo?.values?.[titleKey],
  );

  const patientTitle = useMemo(() => {
    if (title) {
      return title;
    }
    return patientInfo?.values?.[titleKey];
  }, [title, patientInfo]);

  const reminder = usePatientReminder(patientId);
  const openEdit = useCallback(
    (patientId: number) => forms.openPatientEditing(patientId),
    [history],
  );

  const { data: personTitles = [] } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.Titles,
  );

  const doctorTitle = useMemo(() => {
    if (doctorQuery.data?.titleId) {
      const personTitle = personTitles.find(
        (title) => title.id === doctorQuery.data.titleId,
      );
      if (personTitle) {
        return i18nDict.t(personTitle.value as any);
      }
      return '';
    }
    return '';
  }, [doctorQuery.data, personTitles]);

  return (
    <div className={styles.container} data-test-id="patient-info-block">
      <div className={styles.header}>
        <div className={styles.controlsContainer}>
          <div className={styles.patientId} data-test-id="patientInfo-number">
            {patientInfo
              ? localizeNumberWithSource(
                  patientInfo.number,
                  patientInfo.numberSource,
                )
              : null}
            {patientInfo ? (
              <PatientDataReminder
                number={patientInfo.number}
                numberSource={patientInfo.numberSource}
                subtenantId={patientInfo.subtenantId}
                {...reminder}
              />
            ) : null}
          </div>
          <div className={styles.editButtonContainer}>
            {!!patientInfo?.importErrors && (
              <div title={i18ImportError.t('hint')}>
                <WarningIcon className={styles.importErrorsIcon} />
              </div>
            )}
            <UniLink
              title={i18n.t('edit_button')}
              onClick={() => openEdit(patientId)}
              color={ButtonColor.Primary}
            />
          </div>
        </div>
        <div className={styles.name}>
          {patientTitle && (
            <span className={styles.personTitle}>
              {i18nDict.t(patientTitle as any)}
            </span>
          )}
          <span data-test-id="patientInfo-lastFirstName">{`${patientInfo?.lastName} ${patientInfo?.firstName}`}</span>
          <span
            className={styles.subtenantCode}
            data-test-id="patientInfo-subtenantCode"
          >
            {subtenant?.code}
          </span>
        </div>
      </div>
      <div className={styles.patientInformation}>
        <div className={styles.column}>
          <PatientInfoField
            title={i18n.t('date_of_birth')}
            value={getFormattedDate(patientInfo?.dateOfBirth)}
          />
          <PatientInfoField
            title={i18n.t('priority_contact')}
            value={patientInfo?.priorityContact || '-'}
          />
        </div>
        <div className={styles.column}>
          <PatientInfoField
            title={i18n.t('sex')}
            value={patientInfo?.sex ? localizeSexType(patientInfo?.sex) : '-'}
          />
          <PatientInfoField
            className={styles.doctor}
            title={i18n.t('doctor')}
            value={
              doctorQuery.data
                ? [doctorTitle, getDoctorFullName(doctorQuery.data)].join(' ')
                : '-'
            }
          />
        </div>
      </div>
      {patientInfo?.isDeleted && (
        <div
          className={styles.deletedStampContainer}
          data-test-id={
            patientInfo.anonymous ? 'patient-anonimized' : 'patient-archived'
          }
        >
          {patientInfo.anonymous ? (
            <AnonymousStamp className={styles.deletedStamp} />
          ) : (
            <DeletedStamp className={styles.deletedStamp} />
          )}
        </div>
      )}
    </div>
  );
};

interface PatientInfoField {
  title: string;
  value: string;
  className?: string;
}

const PatientInfoField = (field: PatientInfoField) => (
  <Field
    title={field.title}
    className={clsx(styles.patientInfoField, field.className)}
  >
    <span data-test-id="field-value">{field.value}</span>
  </Field>
);
